<template>
  <div>
    <GlobalNavigation />

    <main class="md:py-8 mb-16">
      <!-- Featured -->
      <BaseContainer v-if="featuredEvent && useFeaturedEvent">
        <EventFeatured :eventItem="featuredEvent" />
      </BaseContainer>

      <BaseContainer  v-if="layout === LayoutType.LIST">
        <h2 class="h3 mb-8" v-if="gridItems.length">
          {{ pageTitleText ? pageTitleText : '' }}
        </h2>

        <!-- Vanilla event list -->
        <div class="space-y-4 mb-4">
          <template v-for="item in gridItems" :key="item.type + '-' + item.data.id">
            <EventCard
              v-if="item.type === 'event'"
              list
              :eventItem="item.data as EventItem"
            />
            <CategoryCard
              v-else
              list
              :category="item.data as Category"
            />
          </template>
        </div>
      </BaseContainer>

      <BaseContainer v-if="layout === LayoutType.GRID">
        <h2 class="h3 mb-8" v-if="gridItems.length">
          {{ pageTitleText ? pageTitleText : '' }}
        </h2>

        <!-- Vanilla event grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-30"> 
          <template v-for="item in (useFeaturedEvent ? gridItems.filter(i => i.data.id !== featuredEvent.id) : gridItems)" :key="item.type + '-' + item.data.id">
            <EventCard
              v-if="item.type === 'event'"
              :eventItem="item.data as EventItem"
            />
            <CategoryCard
              v-else
              :category="item.data as Category"
            />
          </template>
        </div>
      </BaseContainer>

      <!-- Segments -->
      <div class="space-y-20 my-20" v-if="layout === LayoutType.SLIDER && segments.length > 0">
        <div v-for="segment in segments" :key="segment.id">
          <BaseSliderSection
            :link="segment.link" 
            :linkText="segment.linkText"
            :title="segment.name"
            :showNavigation="segment.showNavigation"
          >
            <swiper-slide
              v-for="eventItem in segment.events"
              :key="eventItem.id"
              class="h-auto"
            >
              <EventCard
                class="h-full"
                :eventItem="eventItem"
              />
            </swiper-slide>
          </BaseSliderSection>
        </div>
      </div>


      <!-- No results state -->
      <BaseContainer v-if="hasNoResults">
        <div class="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <p class="text-lg font-semibold">{{ $t('globals.no_results') }}</p>
          <button
            class="btn btn-primary whitespace-nowrap inline-flex items-center justify-center border rounded-full font-semibold transition-colors cursor-pointer px-4 py-2 bg-surface-light text-text border-surface-grey hover:opacity-90"
            @click="resetSearch"
          >
            {{ $t('globals.see_all_events') }}
          </button>
        </div>
      </BaseContainer>

      <BaseContainer v-if="doNotShow">
        <div class="max-w-[680px] mx-auto prose prose-lg">
          <h2>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</h2>

          <p>
            Ut ultricies vestibulum lacus in semper consectetur. Donec non
            tellus ut enim aliquet lacinia. Aenean ac orci id elit molestudas
            rhoncus porttitor eget lacus. Quisque a justo tristique, congue
            dolor et, scelerisque velit. Mauris eget dictum pulvinar. Sed
            bibendum ut magna ut tincidunt. Praesent pretium ut mi elementum.
            Phasellus condimentum leo quis erat rhoncus, et lacinia mi dictum.
          </p>

          <p>
            Nunc vulputate in augue eu consectetur. Vestibulum suscipit, leo vel
            convallis consectetur, est purus mollis magna, mattis varius
            vestibulum ex ante sit amet metus. Morbi sodales neque vulputate,
            aliquam mi non, pellentesque mi. Curabitur ullamcorper convallis
            eros, at porttitor ex gravida non.
          </p>

          <h3>Sed vestibulum lacus in semper consectetur.</h3>

          <p>
            Nunc vitae consectetur neque, id volutpat magna. Curabitur ut mollis
            neque. Sed consectetur erat ut eleifend accumsan. Donec vehicula
            odio efficitur odio fermentum, at cursus felis vehicula. Vivamus et
            eros purus. Integer posuere placerat aliquet.
          </p>

          <h3>Aliquam ornare nisl ut odio porta tincidunt.</h3>

          <p>
            Maecenas non nunc vel sem rhoncus gravida. Morbi congue, sapien nec
            consectetur pretium, sapien velit at diam, nec ultricies dolor orci
            aliquet orci. Nam posuere risus augue, sed scelerisque risus
            suscipit sit amet. Suspendisse potenti. Duis vel elit fermentum,
            laoreet velit a, rhoncus massa. Praesent tincidunt. Curabitur eu
            faucibus metus. Etiam gravida lorem ipsum.
          </p>

          <h3>
            Suspendisse ultricies venenatis eros, sed tempor dui condimentum et.
          </h3>

          <p>
            Vivamus elementum pellentesque eros, id porttitor augue porta eget.
            Nullam pharetra rutrum viverra. Praesent egestas eget tortor et
            vulputate. Mauris ac mollis urna. Sed dui sapien, rhoncus ut
            imperdiet vel, suscipit at sem. Pellentesque sodales rutrum aliquet.
            Proin malesuada turpis dignissim nisi ullamcorper convallis. Morbi
            luctus, felis et feugiat mattis, arcu magna cursus sem, id imperdiet
            felis libero eu lectus.
          </p>
        </div>
      </BaseContainer>

      <!-- Tenant-defined HTML (superadmin). Frontpage only, but shown even when the event list is empty -->
      <EventListCustomContent v-if="isFrontpageView" />
    </main>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  import CategoryCard from '@/components/event/CategoryCard.vue';
  import EventCard from '@/components/event/EventCard.vue';
  import EventFeatured from '@/components/event/EventFeatured.vue';
  import EventListCustomContent from '@/components/event/EventListCustomContent.vue';
  import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import BaseSliderSection from '@/components/ui/BaseSliderSection.vue';
  import { useGeneralStore } from '@/stores/general';
  import { useToastStore } from '@/stores/toastStore';
  import type { Category, EventItem } from '@/types';
  import { getEventtime } from '@/utils/eventItemUtil';
  import { formatDate } from '@/utils/formatDate';
  import { useOrganizationStructuredData } from '@/utils/structuredData';
  import { unixTimeToDate } from '@/utils/unixTimeToDate';

  const { locale } = useI18n();
  const { t: $t } = useI18n();

  const route = useRoute();
  const router = useRouter();
  const generalStore = useGeneralStore();
  const toastStore = useToastStore();

  // Check localStorage for showing emptied cart toast
  const showEmptiedCartToast = localStorage.getItem('showEmptiedCartToast');
  if (showEmptiedCartToast == 'true') {
    toastStore.showToast(
      $t('common.cart_emptied_toast'),
      10000
    );
    localStorage.removeItem('showEmptiedCartToast');
    // console.log('Showing cart emptied toast');
  }
  // else {
  //   console.log('Not showing cart emptied toast');
  // }
  
  const doNotShow = false; // temporary disable
  
  const props = defineProps<{
    categoryShortURL?: string;
    specialCategoryShortURL?: string;
  }>();

  const headTitle = computed(() => {
    const alias = generalStore.general?.customer.alias;
    return alias ? $t('meta.frontpage_title', { alias }) : '';
  });

  const headDescription = computed(() => {
    const alias = generalStore.general?.customer.alias;
    return alias ? $t('meta.frontpage_description', { alias }) : '';
  });

  useHead({
    title: headTitle,
    meta: [
      {
        name: 'description',
        content: headDescription,
      },
      {
        property: 'og:title',
        content: headTitle,
      },
      {
        property: 'og:description',
        content: headDescription,
      },
    ],
  });

  // Structured data (JSON-LD) for search engines
  const customer = computed(() => generalStore.general?.customer);
  useOrganizationStructuredData(customer);

  // Fetch general data on mount
  onMounted(() => {
    generalStore.fetchGeneral();
  });

  // Determine the page header text
  const pageTitleText = computed<string>(() => {
    // TODO we need to do something about this: when more queries exist the first (handled here) wins. That means that if the user searches for a category and then a location the category name is shown even though the location is the last filter applied. That will confuse some.    

    // Filter by category
    if (route.query.category || props.categoryShortURL) {
      const category = generalStore.categories().find(cat => cat.name === route.query.category || cat.short_url === props.categoryShortURL);
      return category?.name ?? "";
    }

    // Filter by special category
    if (route.query.special_category || props.specialCategoryShortURL) {
      const sc = generalStore.special_categories().find(sc => sc.name === route.query.special_category || sc.short_url === props.specialCategoryShortURL);
      return sc?.name ?? "";
    }

    // Filter by venue
    if (route.query.location) {
      const venue = generalStore.venues().find(ven => ven.name === route.query.location);
      return venue?.name ?? "";
    }
    
    // Filter by search term
    if (route.query.q) {
      const searchTerm = (route.query.q as string).toLowerCase();
      return searchTerm;
    }

    if (route.query.date) {      
      return formatDate(parseInt(route.query.date.toString()), { locale: locale.value, timezone: 'Europe/Copenhagen' });
    }

    return ""; 
  });

  const searchParams = ['category', 'special_category', 'location', 'q', 'date'] as const;

  const hasSearchQuery = computed(() =>
    searchParams.some(param => param in route.query)
  );

  const hasNoResults = computed(() => hasSearchQuery.value && gridItems.value.length === 0);

  // The unfiltered frontpage: no search query and not a category page (those are routed with the short URL props)
  const isFrontpageView = computed(
    () => !hasSearchQuery.value && !props.categoryShortURL && !props.specialCategoryShortURL
  );

  function resetSearch() {
    router.push({ query: {} });
  }

  // Use events from the store
  const eventItems = computed<EventItem[]>(() => generalStore.events());

  // Add useList to control EventCard list prop
  const layout = computed<LayoutType>(() => {
    const customer = generalStore.customer();

    // Check if route.query has any children
    const hasQueryParams = searchParams.some(param => param in route.query);

    // If there are query params it is a search, always use GRID layout
    if (!hasQueryParams && customer.frontpage_type === 2) {
      return LayoutType.SLIDER;
    }

    switch (customer.frontpage_layout_name) {
      case "one_featured":
        return LayoutType.GRID;
      case "side_by_side":
        return LayoutType.GRID;
      case "full_list":
        return LayoutType.LIST;
      case "simple_list":
        return LayoutType.LIST;
      default:
        return LayoutType.GRID;
    }
  });
  
  type FrontpageGridItem = // TODO MOVE
    | { type: 'event'; data: EventItem }
    | { type: 'category'; data: Category };

  // Compute events to be shown on the page
  const gridItems = computed<FrontpageGridItem[]>(() => {
    // console.log("Raw eventItems:", eventItems.value);
    // console.log("generalStore.events:", generalStore.events());
    
    const filteredEvents = eventItems.value
      .filter(event => {
        let isActive = true;
        let isFrontpage = true;

        // Filter out events that don't have the active language
        if (!event.languages.includes(locale.value)) {
          isActive = false;
        }

        // Filter by date
        if (route.query.date) {
          if (!event.frontpage_event_highlight_in_calendar) {
            isActive = false;
          } else {
            const date = unixTimeToDate(parseInt(route.query.date.toString()));
            const eventDate = unixTimeToDate(getEventtime(event));

            if (
              date.getUTCFullYear() !== eventDate.getUTCFullYear() ||
              date.getUTCMonth() !== eventDate.getUTCMonth() ||
              date.getUTCDate() !== eventDate.getUTCDate()
            ) {
              isActive = false;
            }
          }
          isFrontpage = false;
        }

        // Filter by search term
        if (route.query.q) {
          const searchTerm = (route.query.q as string).toLowerCase().trim();
          if (
            !event.info1.toLowerCase().includes(searchTerm) 
            // && !event.description?.toLowerCase().includes(searchTerm)
          ) {
            isActive = false;
          }
          isFrontpage = false;
        }

        // Filter by category
        if (route.query.category || props.categoryShortURL) {
          // Get all the category ids that match the category query (there can be multiple because of duplicate names)
          const matchingCategoryIds = generalStore.categories()
            .filter(cat => cat.name === route.query.category || cat.short_url === props.categoryShortURL)
            .map(cat => String(cat.id));
          // Check if the event has at least one of those category ids
          if (matchingCategoryIds.length === 0 || !event.categories?.map(String).some(id => matchingCategoryIds.includes(id))) {
            isActive = false;
          }
          isFrontpage = false;
        }

        // Filter by special category
        if (route.query.special_category || props.specialCategoryShortURL) {
          const matchingSpecialCategoryIds = generalStore.special_categories()
            .filter(sc => sc.name === route.query.special_category || sc.short_url === props.specialCategoryShortURL)
            .map(sc => String(sc.id));
          if (matchingSpecialCategoryIds.length === 0 || !event.special_categories?.map(String).some(id => matchingSpecialCategoryIds.includes(id))) {
            isActive = false;
          }
          isFrontpage = false;
        }

        // Filter by venue
        if (route.query.location) {
          const matchingVenueIds = generalStore.venues()
            .filter(venue => venue.name === route.query.location)
            .map(venue => venue.id);
          if (!matchingVenueIds.includes(event.venue ?? -1)) {
            isActive = false;
          }
          isFrontpage = false;
        }

        // Check if the event has the active language
        if (!event.languages.includes(locale.value)) {
          isActive = false;
        }

        // Filter out events hidden on the frontpage when there is no search query
        if (isFrontpage && event.hidden_on_frontpage) {
          isActive = false;
        }

        return isActive;
      })
      .sort((a, b) => {
        // NOTE promoting, sorting and 'categories as event cards' is handled serverside by the JSON daemon

        // handle promotion in category search: if category query exists, promote events in that category with category_sort_event.sort_order, then by aggregated_categories_event_order, then maintain initial order. If no category query, maintain initial order
        if (route.query.category) {
          // Get all categories that match the category query (there can be multiple because of duplicate names)
          const matchingCategories = generalStore.categories().filter(cat => cat.name === route.query.category);
          if (matchingCategories.length > 0) {
            // Merge all category_sort_event entries from the matching categories to find the sort_order for the current events.
            const allSortEvents = matchingCategories.flatMap(cat => cat.category_sort_event);
            const sortOrderA = allSortEvents.find(item => item.event_id === a.id)?.sort_order;
            const sortOrderB = allSortEvents.find(item => item.event_id === b.id)?.sort_order;

            // Events with a defined sort order come first, followed by those without
            if (sortOrderA !== undefined && sortOrderA !== null && sortOrderB !== undefined && sortOrderB !== null) {
              return sortOrderA - sortOrderB;
            } else if (sortOrderA !== undefined && sortOrderA !== null) {
              return -1; // a comes before b
            } else if (sortOrderB !== undefined && sortOrderB !== null) {
              return 1; // b comes before a
            } else {
              // Sort by aggregated_categories_event_order if sortOrderA and sortOrderB are null
              const aggregatedOrderA = a.aggregated_categories_event_order;
              const aggregatedOrderB = b.aggregated_categories_event_order;

              if (typeof aggregatedOrderA === 'number' && typeof aggregatedOrderB === 'number') {
                return aggregatedOrderA - aggregatedOrderB;
              } else if (typeof aggregatedOrderA === 'number') {
                return -1; // a comes before b
              } else if (typeof aggregatedOrderB === 'number') {
                return 1; // b comes before a
              }
            }
          }
        }

        // handle sorting in special category search
        if (route.query.special_category || props.specialCategoryShortURL) {
          const matchingSpecialCategories = generalStore.special_categories()
            .filter(sc => sc.name === route.query.special_category || sc.short_url === props.specialCategoryShortURL);
          if (matchingSpecialCategories.length > 0) {
            const allSortEvents = matchingSpecialCategories.flatMap(sc => sc.special_category_sort_events);
            const sortOrderA = allSortEvents.find(item => item.event_id === a.id)?.sort_order;
            const sortOrderB = allSortEvents.find(item => item.event_id === b.id)?.sort_order;

            if (sortOrderA !== undefined && sortOrderA !== null && sortOrderB !== undefined && sortOrderB !== null) {
              return sortOrderA - sortOrderB;
            } else if (sortOrderA !== undefined && sortOrderA !== null) {
              return -1;
            } else if (sortOrderB !== undefined && sortOrderB !== null) {
              return 1;
            }
          }
        }

        // If no category query, maintain the initial order
        return 0;
      })

    // Wrap each filtered event as a FrontpageGridItem
    const result: FrontpageGridItem[] = filteredEvents.map(event => ({ type: 'event' as const, data: event }));

    // Merge promoted categories into the list at their promoted index (only on unfiltered frontpage)
    const hasQueryParams = searchParams.some(param => param in route.query);
    if (!hasQueryParams) {
      const allCategories = generalStore.categories();
      const promotedList = generalStore.promoted();

      // Build a list of promoted categories with their sort order derived from the promoted array index (this sorting code is brought to you by AI)
      const promotedCategories = promotedList
        .map((entry, index) => ({ entry, sortOrder: index }))
        .filter(({ entry }) => entry.element_type === 'category')
        .map(({ entry, sortOrder }) => {
          const category = allCategories.find(c => c.id === entry.element_id);
          return category ? { category, sortOrder } : null;
        })
        .filter((item): item is { category: Category; sortOrder: number } =>
          item !== null && item.category.promoted && item.category.languages.includes(locale.value)
        )
        .sort((a, b) => a.sortOrder - b.sortOrder);

      // Insert in reverse so earlier inserts don't shift the indices of later ones
      for (const { category, sortOrder } of [...promotedCategories].reverse()) {
        const index = Math.min(sortOrder, result.length);
        result.splice(index, 0, { type: 'category', data: category });
      }
    }

    return result;
  });

  // Determine if we should show the featured event
  const useFeaturedEvent = computed<boolean>(() => {
    return shouldUseFeaturedEvent();
  });

  function shouldUseFeaturedEvent(): boolean {
    const hasQueryParams = Object.keys(route.query).length > 0;
    if (hasQueryParams || props.categoryShortURL) {
      return false;
    }

    if (
      generalStore.customer().frontpage_layout_name === "one_featured" &&
      generalStore.customer().frontpage_type === 1
    ) {
      return true;
    }
    return false;
  }

  // Pick the featured event
  // Notice we take the featured event from eventItems not events. This is because we remove the featured event from events so it doesn't appear in the grid/list. This relies on the fact that JSON is sorted correctly for the frontpage AND that eventItems is never sorted on the frontpage (its fine to sort eventItems when searching because we don't use featured event with search).
  const featuredEvent = computed<EventItem>(() => gridItems.value.find(item => item.type === 'event')?.data as EventItem); 

  const frontpage2_segments = computed(() => generalStore.frontpage2_segments());

  // Compute segments to be shown on the page
  const segments = computed(() =>
    frontpage2_segments.value
      .map(segment => {
        const event_ids = segment.events;

        const active_locale = locale.value;

        // Get all the eventItems from eventItems based on event_ids
        const segmentEvents = eventItems.value
          .filter(event => event_ids.includes(Number(event.id)))
          .filter(event => event.languages.includes(active_locale));

        // Get segment name based on language
        const name = segment.localizations[active_locale]?.name || '';
        
        // Determine link based on category_id
        let link;
        let linkText;
        if (segment.category_id) {
          const category = generalStore.categories().find(cat => cat.id === segment.category_id);
          if (category) {
            link = `?category=${encodeURIComponent(category.name)}`;
            linkText = 'common.see_category';
          }
        }

        const showNavigation = segmentEvents.length >= 4;

        // Add those to the segment object
        return {
          ...segment,
          events: segmentEvents,
          name: name,
          link: link,
          linkText: linkText,
          showNavigation: showNavigation,
        };
      })
      .filter(segment => segment.events.length > 0 && segment.languages.includes(locale.value)) // Ensure segment is available in the active language
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) // Sort by segment.sort_order
  );


  // // Build the interleaved grid using the promoted list to weave events and categories
  // const gridItems = computed<FrontpageGridItem[]>(() => {
  //   const promoted = generalStore.promoted();
  //   const hasQueryParams = ['category', 'location', 'q', 'date'].some(param => param in route.query);

  //   // When searching/filtering, only show events (no category cards)
  //   if (hasQueryParams || promoted.length === 0) {
  //     return events.value;
  //   }

  //   // Build combined list from promoted order
  //   const allCategories = generalStore.categories();
  //   const result: FrontpageGridItem[] = [];
  //   const usedEventIds = new Set<number>();

  //   // Extract the EventItem entries from events for easy lookup
  //   const eventItemsList = events.value
  //     .filter((item): item is { type: 'event'; data: EventItem } => item.type === 'event');

  //   for (const entry of promoted) {
  //     if (entry.element_type === 'event') {
  //       const eventItem = eventItemsList.find(e => e.data.id === entry.element_id);
  //       if (eventItem) {
  //         result.push(eventItem);
  //         usedEventIds.add(eventItem.data.id);
  //       }
  //     } else if (entry.element_type === 'category') {
  //       const category = allCategories.find(c => c.id === entry.element_id);
  //       if (category && category.promoted && category.languages.includes(locale.value)) {
  //         result.push({ type: 'category', data: category });
  //       }
  //     }
  //   }

  //   // Append remaining events that weren't in the promoted list
  //   for (const eventItem of eventItemsList) {
  //     if (!usedEventIds.has(eventItem.data.id)) {
  //       result.push(eventItem);
  //     }
  //   }

  //   return result;
  // });

  enum LayoutType {
    GRID = 0,
    LIST = 1,
    SLIDER = 2,
  }

</script>
